import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, IconButton, Tooltip, Chip, CircularProgress, Alert, MenuItem, Divider,
  FormControlLabel, Checkbox, InputAdornment,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import Email from '@mui/icons-material/Email';
import Phone from '@mui/icons-material/Phone';
import VpnKey from '@mui/icons-material/VpnKey';
import { techniciansAPI } from '../../services/api';
import TechnicianAccessPanel from '../../components/common/TechnicianAccessPanel';

const RESOURCE_TYPES = [
  { value: 'internal', label: 'Personal propio' },
  { value: 'external', label: 'Autonomo / externo' },
  { value: 'company', label: 'Empresa colaboradora' },
];

const ASSIGNMENT_TYPE_LABELS = {
  work: 'Trabajo', travel: 'Desplazamiento', training: 'Formacion',
  vacation: 'Vacaciones', absence: 'Ausencia / baja',
};

const EMPTY_FORM = {
  code: '', name: '', resource_type: 'internal', company_name: '', role: '',
  discipline: '', color: '#1565C0', email: '', phone: '', address: '', city: '',
  country: 'Espana', nif: '', hourly_cost: '', daily_hours: 8,
  start_date: '', end_date: '', is_active: true, notes: '',
};

const fmtDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '-');
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtNum = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('es-ES'));

export default function Technicians() {
  const [technicians, setTechnicians] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [accessDialog, setAccessDialog] = useState(null);   // tecnico o null

  const loadTechnicians = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await techniciansAPI.list(search ? { q: search } : undefined);
      setTechnicians(resp.data);
      setError(null);
    } catch (err) {
      console.error('Error loading technicians:', err);
      setError('Error al cargar los tecnicos.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(loadTechnicians, 300);
    return () => clearTimeout(timer);
  }, [loadTechnicians]);

  const loadSummary = async (id) => {
    try {
      setLoadingSummary(true);
      const resp = await techniciansAPI.summary(id);
      setSummary(resp.data);
    } catch (err) {
      console.error('Error loading technician summary:', err);
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSelect = (t) => {
    setSelected(t);
    loadSummary(t.id);
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      ...EMPTY_FORM,
      ...Object.fromEntries(Object.entries(t).map(([k, v]) => [k, v === null ? '' : v])),
      is_active: !!t.is_active,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    const payload = {
      ...form,
      hourly_cost: form.hourly_cost === '' ? null : parseFloat(form.hourly_cost),
      daily_hours: form.daily_hours === '' ? null : parseFloat(form.daily_hours),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };
    try {
      if (editing) {
        await techniciansAPI.update(editing.id, payload);
      } else {
        await techniciansAPI.create(payload);
      }
      setDialogOpen(false);
      await loadTechnicians();
      if (editing && selected?.id === editing.id) loadSummary(editing.id);
    } catch (err) {
      console.error('Error saving technician:', err);
      setFormError(err.response?.data?.detail || 'Error al guardar el tecnico.');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await techniciansAPI.delete(confirmDelete.id);
      if (selected?.id === confirmDelete.id) {
        setSelected(null);
        setSummary(null);
      }
      setConfirmDelete(null);
      await loadTechnicians();
    } catch (err) {
      console.error('Error deleting technician:', err);
      setError('Error al eliminar el tecnico.');
    }
  };

  const isOnLeave = (t) => {
    const day = todayISO();
    if (!t.is_active) return true;
    if (t.end_date && t.end_date < day) return true;
    if (t.start_date && t.start_date > day) return true;
    return false;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small" placeholder="Buscar tecnico, empresa o rol..." value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={openNew}>Nuevo tecnico</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                {['Codigo', 'Nombre', 'Tipo', 'Rol', 'Contacto', 'Alta', 'Baja', 'Estado', 'Acceso app', ''].map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {technicians.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">
                      No hay tecnicos en el repertorio. Anade los tecnicos fijos con su fecha de alta y baja.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : technicians.map((t) => (
                <TableRow
                  key={t.id} hover selected={selected?.id === t.id}
                  sx={{ cursor: 'pointer' }} onClick={() => handleSelect(t)}
                >
                  <TableCell>{t.code || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color }} />
                      <strong>{t.name}</strong>
                      {t.company_name && (
                        <Typography variant="caption" color="textSecondary">({t.company_name})</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {RESOURCE_TYPES.find((r) => r.value === t.resource_type)?.label || t.resource_type}
                  </TableCell>
                  <TableCell>{t.role || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {t.email && (
                        <Tooltip title={t.email}><Email fontSize="small" color="action" /></Tooltip>
                      )}
                      {t.phone && (
                        <Tooltip title={t.phone}><Phone fontSize="small" color="action" /></Tooltip>
                      )}
                      {!t.email && !t.phone && '-'}
                    </Box>
                  </TableCell>
                  <TableCell>{fmtDate(t.start_date)}</TableCell>
                  <TableCell>{fmtDate(t.end_date)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={isOnLeave(t) ? 'No disponible' : 'Disponible'}
                      color={isOnLeave(t) ? 'default' : 'success'}
                    />
                  </TableCell>
                  {/* El codigo de entrada, a la vista en el listado: es el dato
                      que hay que dictarle al tecnico y antes quedaba escondido */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {t.app_access_enabled ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" color="primary" label={t.login_code}
                          sx={{ fontWeight: 'bold', letterSpacing: 1 }} />
                        {t.is_team_leader && (
                          <Chip size="small" variant="outlined" label="jefe" />
                        )}
                      </Box>
                    ) : (
                      <Button size="small" startIcon={<VpnKey fontSize="small" />}
                        onClick={() => setAccessDialog(t)}>
                        Dar acceso
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Acceso a la app">
                      <IconButton size="small" color={t.app_access_enabled ? 'primary' : 'default'}
                        onClick={() => setAccessDialog(t)}>
                        <VpnKey fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirmDelete(t)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ficha del tecnico seleccionado */}
      {selected && (
        <Paper sx={{ p: 2.5 }}>
          {loadingSummary || !summary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: summary.technician.color }} />
                <Typography variant="h6" fontWeight="bold">{summary.technician.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {summary.technician.role}{summary.technician.discipline ? ` · ${summary.technician.discipline}` : ''}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" startIcon={<Edit />} onClick={() => openEdit(summary.technician)}>
                  Editar ficha
                </Button>
              </Box>

              <Grid container spacing={2}>
                {/* Datos de contacto */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Datos de contacto</Typography>
                  <Table size="small">
                    <TableBody>
                      {[
                        ['Codigo', summary.technician.code],
                        ['Tipo', RESOURCE_TYPES.find((r) => r.value === summary.technician.resource_type)?.label],
                        ['Empresa', summary.technician.company_name],
                        ['NIF', summary.technician.nif],
                        ['Email', summary.technician.email],
                        ['Telefono', summary.technician.phone],
                        ['Direccion', summary.technician.address],
                        ['Poblacion', summary.technician.city],
                        ['Pais', summary.technician.country],
                        ['Alta', fmtDate(summary.technician.start_date)],
                        ['Baja', fmtDate(summary.technician.end_date)],
                        ['Coste / hora', summary.technician.hourly_cost ? `${fmtNum(summary.technician.hourly_cost)} EUR` : null],
                        ['Jornada', summary.technician.daily_hours ? `${summary.technician.daily_hours} h/dia` : null],
                      ].map(([label, value]) => (
                        <TableRow key={label}>
                          <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.3, pl: 0 }}>{label}</TableCell>
                          <TableCell sx={{ border: 0, py: 0.3 }}>{value || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {summary.technician.notes && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="textSecondary">{summary.technician.notes}</Typography>
                    </>
                  )}
                </Grid>

                {/* Sumatorio de horas */}
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Sumatorio de horas</Typography>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {[
                      ['Horas productivas', fmtNum(summary.totals.hours)],
                      ['Dias trabajados', fmtNum(summary.totals.days)],
                      ['Proyectos', fmtNum(summary.totals.projects)],
                      ['Horas no productivas', fmtNum(summary.totals.absence_hours)],
                      ['Coste imputado', summary.totals.cost !== null ? `${fmtNum(summary.totals.cost)} EUR` : '-'],
                    ].map(([label, value]) => (
                      <Grid item xs={6} sm={4} md={2.4} key={label}>
                        <Paper variant="outlined" sx={{ p: 1.2, textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold">{value}</Typography>
                          <Typography variant="caption" color="textSecondary">{label}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Proyectos en los que ha intervenido */}
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Proyectos en los que ha intervenido
                  </Typography>
                  {summary.by_project.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Todavia no tiene asignaciones registradas.
                    </Typography>
                  ) : (
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Proyecto</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Ordenes de trabajo</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Dias</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Horas</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Coste</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.by_project.map((p) => (
                          <TableRow key={p.project_id || 'none'}>
                            <TableCell>
                              <strong>{p.project_code || '-'}</strong> {p.project_name}
                            </TableCell>
                            <TableCell>{p.orders.join(', ') || '-'}</TableCell>
                            <TableCell align="right">{fmtNum(p.days)}</TableCell>
                            <TableCell align="right"><strong>{fmtNum(p.hours)}</strong></TableCell>
                            <TableCell align="right">{p.cost !== null ? `${fmtNum(p.cost)} EUR` : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  {/* Horas por mes */}
                  {summary.by_month.length > 0 && (
                    <>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Horas por mes</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {summary.by_month.map((m) => (
                          <Chip key={m.month} size="small" variant="outlined"
                            label={`${m.month}: ${fmtNum(m.hours)} h (${m.days} d)`} />
                        ))}
                      </Box>
                    </>
                  )}

                  {/* Asignaciones */}
                  {summary.assignments.length > 0 && (
                    <>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Asignaciones</Typography>
                      <TableContainer sx={{ maxHeight: 260 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Desde</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Hasta</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Proyecto / OF</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Tarea</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }} align="right">Horas</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.assignments.map((a) => (
                              <TableRow key={a.id}>
                                <TableCell>{fmtDate(a.start_date)}</TableCell>
                                <TableCell>{fmtDate(a.end_date)}</TableCell>
                                <TableCell>{ASSIGNMENT_TYPE_LABELS[a.assignment_type] || a.assignment_type}</TableCell>
                                <TableCell>
                                  {a.project_code || '-'}
                                  {a.manufacturing_order_code ? ` / ${a.manufacturing_order_code}` : ''}
                                </TableCell>
                                <TableCell>{a.task || '-'}</TableCell>
                                <TableCell align="right">{fmtNum(a.hours)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Dialogo de tecnico */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Editar ${editing.name}` : 'Nuevo tecnico'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Codigo" value={form.code} onChange={setField('code')}
                helperText="Se genera si se deja vacio" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth required label="Nombre" value={form.name} onChange={setField('name')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Tipo de recurso" value={form.resource_type} onChange={setField('resource_type')}>
                {RESOURCE_TYPES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Empresa" value={form.company_name} onChange={setField('company_name')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Rol" value={form.role} onChange={setField('role')}
                placeholder="jefe de mecanicos, mecanico, electricista..." />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Disciplina" value={form.discipline} onChange={setField('discipline')}
                placeholder="mecanica, electrica, instrumentacion..." />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">Contacto</Typography></Divider></Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Email" value={form.email} onChange={setField('email')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Telefono" value={form.phone} onChange={setField('phone')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="NIF" value={form.nif} onChange={setField('nif')} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth label="Direccion" value={form.address} onChange={setField('address')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Poblacion" value={form.city} onChange={setField('city')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Pais" value={form.country} onChange={setField('country')} />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">Disponibilidad y coste</Typography></Divider></Grid>

            <Grid item xs={12} sm={3}>
              <TextField fullWidth type="date" label="Fecha de alta" value={form.start_date}
                onChange={setField('start_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth type="date" label="Fecha de baja" value={form.end_date}
                onChange={setField('end_date')} InputLabelProps={{ shrink: true }}
                helperText="Vacio = sin baja prevista" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth type="number" label="Coste / hora" value={form.hourly_cost}
                onChange={setField('hourly_cost')} inputProps={{ step: 0.5, min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth type="number" label="Horas / dia" value={form.daily_hours}
                onChange={setField('daily_hours')} inputProps={{ step: 0.5, min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth type="color" label="Color" value={form.color || '#1565C0'}
                onChange={setField('color')} InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={form.is_active} onChange={setField('is_active')} />}
                label="Activo (aparece en el calendario)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notas" value={form.notes} onChange={setField('notes')} />
            </Grid>
            {/* El acceso a la app se gestiona en su propio dialogo, desde el
                boton de la llave del listado: aqui quedaba enterrado al final
                de un formulario largo y no se veia. */}
            {editing && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Button startIcon={<VpnKey />} variant="outlined"
                  onClick={() => { setDialogOpen(false); setAccessDialog(editing); }}>
                  Acceso a la app de tecnicos
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Acceso a la app: dialogo propio, para que el codigo y el PIN se vean
          de entrada y no haya que bucear en el formulario del tecnico */}
      <Dialog open={!!accessDialog} onClose={() => setAccessDialog(null)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Acceso a la app — {accessDialog?.name}</DialogTitle>
        <DialogContent dividers>
          {accessDialog && (
            <TechnicianAccessPanel technician={accessDialog} onChanged={loadTechnicians} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmacion de borrado */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Eliminar tecnico</DialogTitle>
        <DialogContent>
          <Typography>
            Se eliminara <strong>{confirmDelete?.name}</strong> y todas sus asignaciones del calendario.
            Esta accion no se puede deshacer.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Si solo ha dejado de trabajar con nosotros, es mejor indicar su fecha de baja: asi se conserva
            el historico de horas y proyectos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
